// @vendors
import { useContext, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import moment from 'moment';

// @queries
import { GET_LOAD_TENDER_DETAILS } from 'api/queries';

// @context
import { UserContext } from 'components/Providers/Providers';

// @types
import type { loadTenderType } from 'sharedTypes/loads/loadType';
import { UseRequests } from '../types';

interface UseRequestsParams {
  carrierId: number;
  shouldBeBooked: boolean;
  tenderNumber: number;
}

export function useRequests({ carrierId, tenderNumber, shouldBeBooked }: UseRequestsParams): UseRequests {
  const currentUser = useContext(UserContext);
  const [initialLoadingMessage, setInitialLoadingMessage] = useState('Getting Tender Details');
  const [isLoadExpired, setIsLoadExpired] = useState<boolean | null>(null);

  const wasTenderMarkedToBeDeclined = isLoadExpired !== null
    ? shouldBeBooked === false && !isLoadExpired
    : null;

  const wasTenderMarkedToBeBooked = isLoadExpired !== null
    ? shouldBeBooked === true && !isLoadExpired
    : null;

  const {
    error: errorLoadTenderDetails,
    data: loadTenderDetailsData = { getLoadTenderDetails: {} as loadTenderType }
  } = useQuery(GET_LOAD_TENDER_DETAILS, {
    variables: { tenderId: tenderNumber },
    skip: isNaN(tenderNumber) || isNaN(carrierId) || !currentUser.UserId,
    fetchPolicy: 'no-cache'
  });


  const tenderDetails = Object.keys(loadTenderDetailsData.getLoadTenderDetails).length
    ? loadTenderDetailsData.getLoadTenderDetails
    : null;

  useEffect(() => {
    if (tenderDetails && isLoadExpired === null) {
      setInitialLoadingMessage('Validating expiration date');
      const updatedIsLoadExpired = tenderDetails.offerExpirationDate
        ? moment.utc(tenderDetails.offerExpirationDate).local().isBefore(moment())
        : false;
      setIsLoadExpired(updatedIsLoadExpired);
      setInitialLoadingMessage('')
    }
  }, [isLoadExpired, tenderDetails]);

  return {
    errorLoadTenderDetails,
    initialLoadingMessage,
    isLoadExpired,
    setInitialLoadingMessage,
    tenderDetails,
    wasTenderMarkedToBeBooked,
    wasTenderMarkedToBeDeclined
  };
}
